import React, { useState, useEffect } from 'react';
import { Info, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { integrationService } from '../../services/integrationService';

const WebflowForm = ({ formData, setFormData, isSubmitting, handleCreateIntegration }) => {
  const [sites, setSites] = useState([]);
  const [collections, setCollections] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  // Verify API token and fetch sites
  const verifyApiToken = async () => {
    if (!formData.api_token) return;

    setIsLoadingSites(true);
    setVerificationStatus(null);

    try {
      const result = await integrationService.verifyWebflowCredentials({
        api_token: formData.api_token
      });

      if (result.valid) {
        setSites(result.sites || []);
        setVerificationStatus('success');
      } else {
        setVerificationStatus('error');
      }
    } catch (error) {
      console.error('Error verifying Webflow API token:', error);
      setVerificationStatus('error');
    } finally {
      setIsLoadingSites(false);
    }
  };

  // Fetch collections when site is selected
  const fetchCollections = async () => {
    if (!formData.api_token || !formData.site_id) return;

    setIsLoadingCollections(true);

    try {
      const result = await integrationService.getWebflowCollections({
        api_token: formData.api_token,
        site_id: formData.site_id
      });

      setCollections(result.collections || []);
    } catch (error) {
      console.error('Error fetching Webflow collections:', error);
    } finally {
      setIsLoadingCollections(false);
    }
  };

  // Handle API token change
  const handleApiTokenChange = (e) => {
    setFormData(prev => ({
      ...prev,
      api_token: e.target.value
    }));
  };

  // Handle site selection
  const handleSiteChange = (e) => {
    setFormData(prev => ({
      ...prev,
      site_id: e.target.value
    }));
  };

  // Effects
  useEffect(() => {
    if (formData.api_token && formData.api_token.length > 10) {
      verifyApiToken();
    }
  }, [formData.api_token]);

  useEffect(() => {
    if (formData.site_id) {
      fetchCollections();
    }
  }, [formData.site_id]);

  return (
    <form onSubmit={handleCreateIntegration} className="space-y-6">
      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Integration Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md p-2"
          placeholder="My Webflow Website"
          value={formData.name || ''}
          onChange={(e) => setFormData(prev => ({
            ...prev,
            name: e.target.value
          }))}
          required
        />
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          API Token <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="password"
            className={`w-full border ${verificationStatus === 'error' ? 'border-red-500' : 'border-gray-300'} rounded-md p-2`}
            placeholder="Paste your Webflow API token"
            value={formData.api_token || ''}
            onChange={handleApiTokenChange}
            required
          />
          {verificationStatus === 'success' && (
            <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500" size={18} />
          )}
          {verificationStatus === 'error' && (
            <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500" size={18} />
          )}
        </div>
        <div className="text-sm text-gray-600 mt-1 flex items-center">
          <Info size={16} className="mr-1" />
          <a
            href="https://webflow.com/dashboard/account/apikeys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline flex items-center"
          >
            Get your API token from Webflow dashboard <ExternalLink size={14} className="ml-1" />
          </a>
        </div>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Webflow Site <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            className="w-full border border-gray-300 rounded-md p-2 appearance-none bg-white pr-8"
            value={formData.site_id || ''}
            onChange={handleSiteChange}
            disabled={!sites.length || isLoadingSites}
            required
          >
            <option value="">Choose Webflow Site</option>
            {sites.map(site => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {isLoadingSites && (
            <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Webflow Collection <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            className="w-full border border-gray-300 rounded-md p-2 appearance-none bg-white pr-8"
            value={formData.collection_id || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              collection_id: e.target.value
            }))}
            disabled={!collections.length || isLoadingCollections}
            required
          >
            <option value="">Choose Webflow Collection</option>
            {collections.map(collection => (
              <option key={collection.id} value={collection.id}>{collection.name}</option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {isLoadingCollections && (
            <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-2">
          Publishing Behavior <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <select
            className="w-full border border-gray-300 rounded-md p-2 appearance-none bg-white pr-8"
            value={formData.publishing_behavior || 'draft'}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              publishing_behavior: e.target.value
            }))}
            required
          >
            <option value="publish">Publish Immediately</option>
            <option value="draft">Save as Draft</option>
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
    </form>
  );
};

export default WebflowForm;

